/**
 * SWIPER
 */

// core version + navigation, pagination modules:
import Swiper, {
	Navigation,
	Pagination,
	Thumbs,
	Zoom,
	Autoplay,
} from 'swiper'

// configure Swiper to use modules
Swiper.use([Autoplay, Navigation, Pagination, Thumbs, Zoom])

// Tracking dei soli banner principali della home (GTM dataLayer).
// Nessuna chiamata diretta a GA4: invio e consenso restano gestiti da GTM.
function trackHomeBanners(swiper, node) {
	if (!swiper || !swiper.el || node.id !== 'j-carousel-banner') return
	const seen = new Set()
	let timer = null
	let pendingImage = null
	const ratios = new Map()
	const language = document.documentElement.lang || location.pathname.split('/')[1] || 'it'

	function metadata(slide) {
		const image = slide && slide.querySelector('.c-banner__img')
		if (!image) return null
		const bannerId = slide.getAttribute('data-banner-id')
		if (!bannerId) return null
		const imageUrl = new URL(image.getAttribute('src'), location.href)
		const position = Number(slide.getAttribute('data-banner-position'))
		return {
			banner_id: bannerId,
			banner_name: slide.getAttribute('data-banner-name') || image.getAttribute('alt') || bannerId,
			banner_position: position,
			banner_language: language,
			banner_image: imageUrl.href,
		}
	}

	function send(event, slide, url = '') {
		const data = metadata(slide)
		if (!data) return
		window.dataLayer = window.dataLayer || []
		window.dataLayer.push({ event, ...data, banner_url: url })
	}

	function cancel() {
		clearTimeout(timer)
		timer = null
		pendingImage = null
	}

	function candidate() {
		if (document.hidden || swiper.destroyed || swiper.animating) return null
		const slide = swiper.slides[swiper.activeIndex]
		const image = slide && slide.querySelector('.c-banner__img')
		const data = metadata(slide)
		if (!image || !image.complete || !image.naturalWidth || !data) return null
		if (seen.has(JSON.stringify([data.banner_id, data.banner_image])) || (ratios.get(image) || 0) < 0.5) return null
		return { slide, image, data }
	}

	function schedule() {
		const next = candidate()
		if (next && next.image === pendingImage) return
		cancel()
		if (!next) return
		pendingImage = next.image
		timer = setTimeout(() => {
			const current = candidate()
			cancel()
			if (!current || current.image !== next.image) return
			seen.add(JSON.stringify([current.data.banner_id, current.data.banner_image]))
			send('home_banner_view', current.slide)
		}, 1000)
	}

	// Un solo listener delegato gestisce immagine, CTA e copie create dal loop.
	function click(event) {
		if (event.defaultPrevented || swiper.allowClick === false) return
		const link = event.target.closest && event.target.closest('a[href]')
		const slide = link && link.closest('.swiper-slide')
		if (!slide || !swiper.el.contains(slide)) return
		send('home_banner_click', slide, link.href)
	}
	node.addEventListener('click', click)

	let observer = null
	if ('IntersectionObserver' in window) {
		observer = new IntersectionObserver(entries => {
			entries.forEach(entry => ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0))
			schedule()
		}, { threshold: [0, 0.5, 1] })
		swiper.el.querySelectorAll('.c-banner__img').forEach(image => {
			observer.observe(image)
		})
		swiper.el.addEventListener('load', schedule, true)
		document.addEventListener('visibilitychange', schedule)
		swiper.on('slideChange', () => { cancel(); schedule() })
		swiper.on('beforeTransitionStart', cancel)
		swiper.on('transitionEnd', schedule)
		schedule()
	}

	swiper.on('destroy', () => {
		cancel()
		if (observer) observer.disconnect()
		node.removeEventListener('click', click)
		swiper.el.removeEventListener('load', schedule, true)
		document.removeEventListener('visibilitychange', schedule)
	})
}

const carousels = [...document.getElementsByClassName('j-carousel')]

carousels.forEach(function (node) {
	// config
	const swiperConfig = {
		pagination: {
			el: `#${node.id} .swiper-pagination`,
			clickable: true,
		},
		navigation: {
			nextEl: `#${node.id} .swiper-button-next`,
			prevEl: `#${node.id} .swiper-button-prev`,
		},
		watchOverflow: true,
		
		threshold: 8,
	}

	// Swiper default

	/**
	 * Default
	 *
	 * mobile = r2 x c2
	 * tablet = r1 x c3
	 * desktop = r1 x c4
	 *
	 * Note: to scroll an entire visible page, `slidesPerGroup` = `slidesPerView`
	 */
	new Swiper(`#${node.id} .j-swiper`, {
		...swiperConfig,
		slidesPerView: 2, // cols
		slidesPerGroup: 2,
		slidesPerColumn: 2, // rows
		slidesPerColumnFill: 'row', // note: we need it to use slidesPerColumn
		breakpoints: {
			768: {
				slidesPerView: 3, // cols
				slidesPerGroup: 3,
				slidesPerColumn: 1, // rows
			},
			1024: {
				slidesPerView: 4, // cols
				slidesPerGroup: 4,
				slidesPerColumn: 1, // rows
			},
		},
	})

	/**
	 * Small
	 *
	 * mobile = r1 x c2
	 * desktop = r2 x c1
	 */

	new Swiper(`#${node.id} .j-swiper-small`, {
		...swiperConfig,
		slidesPerView: 2, // cols
		slidesPerGroup: 2,
		slidesPerColumn: 1, // rows
		breakpoints: {
			1024: {
				slidesPerView: 1, // cols
				slidesPerGroup: 1,
				slidesPerColumn: 2, // rows
				slidesPerColumnFill: 'row', // note: we need it to use slidesPerColumn
			},
		},
	})

	/**
	 * Grid
	 *
	 * mobile = r2 x c4
	 * desktop = r2 x c8
	 */

	new Swiper(`#${node.id} .j-swiper-grid`, {
		...swiperConfig,
		slidesPerView: 2, // cols
		slidesPerGroup: 2,
		slidesPerColumn: 2, // rows
		slidesPerColumnFill: 'row', // note: we need it to use slidesPerColumn
		breakpoints: {
			1024: {
				slidesPerView: 4, // cols
				slidesPerGroup: 4,
				slidesPerColumn: 2, // rows
			},
		},
	})

	/**
	 * Highlight
	 *
	 * mobile = r2 x c2
	 * desktop = r2 x c2
	 */

	new Swiper(`#${node.id} .j-swiper-highlight`, {
		...swiperConfig,
		slidesPerView: 2, // cols
		slidesPerGroup: 2,
		slidesPerColumn: 2, // rows
		slidesPerColumnFill: 'row', // note: we need it to use slidesPerColumn
	})

	/**
	 * Banner
	 *
	 * mobile = r1 x c1
	 * desktop = r1 x c1
	 */

	const bannerSwiper = new Swiper(`#${node.id} .j-swiper-banner`, {
		...swiperConfig,
		navigation: false, // TODO: hide navigation does not work properly. This flag disable navigation buttons but does not hide it. Check this.
		loop: true,
		autoplay: {
			delay: 5000,
		},
	})

	trackHomeBanners(bannerSwiper, node)

	// end forEach
})

/**
 * PRODUCT CAROUSEL
 */

const galleryThumbs = new Swiper('.j-product-carousel-thumbs', {
	slidesPerView: 4,
	freeMode: true,
	watchSlidesVisibility: true,
	watchSlidesProgress: true,
})

/* eslint-disable-next-line */
const galleryProduct = new Swiper('.j-product-carousel-gallery', {
	zoom: true,
	navigation: {
		nextEl: '.swiper-button-next',
		prevEl: '.swiper-button-prev',
	},
	pagination: {
		el: '.swiper-pagination',
		type: 'fraction',
	},
	thumbs: {
		swiper: galleryThumbs, // call swiper instance declared before
	},
})
